import React, { useCallback, useEffect, useState } from "react"
import { ScrollView, View, Text } from "react-native"
import { FloatTextInput, HoldingRow, InfoList } from "@atomic"
import { InfoBox } from "@atomic/molecules/InfoBox"
import { useDispatch, useSelector } from "react-redux"
import {
  AccountActions,
  AccountThunks,
  BadgeLayouts,
  HomeScreens,
} from "@modules"
import { I18n } from "react-redux-i18n"
import { Colors } from "@constants"
import { Title13, Title18 } from "@stylesheets"
import { displayToastError, TestIDs } from "@utils"
import _ from "lodash"
import { CashSystem, Holding } from "@foodi/core"
import { GlobalState } from "@redux"
import { useDevices } from "@hooks"
import { unstable_batchedUpdates } from "react-dom"
import { Button } from "@modules/profile/screens/SettingsScreen/MfaSetup/Button"
import { NavigationProp } from "@react-navigation/core"
import { EstablishmentSelectionStyles, inputContainerStyles } from "./styles"
import useBadgeManagement from "@hooks/useBadgeManagement"

type EstablishmentSelectionProps = {
  navigation: NavigationProp<any>
}

export const EstablishmentSelection: React.FC<EstablishmentSelectionProps> = ({
  navigation,
}) => {
  const dispatch = useDispatch()
  const [isMobile] = useDevices()
  const [isSearching, setIsSearching] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [holdingsError, setHoldingsError] = useState<boolean>(false)
  const [isHoldingsListShown, setHoldingsListShown] = useState(false)
  const [localSelectedHolding, setLocalSelectedHolding] = useState<
    Holding | undefined
  >(undefined)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isScolapassHolding, setIsScolapassHolding] = useState(false);

  const infoListItems = [
    I18n.t("associationBadge.establishmentSelection.tipsList.0"),
    I18n.t("associationBadge.establishmentSelection.tipsList.1"),
    I18n.t("associationBadge.establishmentSelection.tipsList.2"),
  ]

  const userInfo = useSelector((state: GlobalState) => state.auth.userInfo)

  const { sendBadge } = useBadgeManagement()

  const {
    hasBadge,
    isEditionMode,
    editionHoldingView,
    selectedHolding,
  } = useSelector((state: GlobalState) => state.account)

  useEffect(() => {
    if (searchValue.length > 2 && !localSelectedHolding)
      getNearHoldingsDebounce(searchValue)
    else setHoldings([])
  }, [searchValue])

  useEffect(() => {
    setHoldingsListShown(holdings.length > 0)
  }, [holdings])

  const getNearHoldingsDebounce = useCallback(
    _.debounce(getNearHoldings, 500),
    []
  )

  async function getNearHoldings(searchText: string) {
    setIsSearching(true)
    const result: any = await dispatch(
      AccountThunks.getNearHoldings({
        searchText: `${searchText}%`,
        email: userInfo?.email,
        withDistance: false,
        order: "name",
      })
    )
    if (result.error) {
      displayToastError(dispatch, I18n.t("associationBadge.toastGlobalError"))
    } else if (result.holdingInfo.length === 0) {
      setHoldingsError(true)
      setHoldingsListShown(false)
    } else {
      setHoldings(result?.holdingInfo || [])
    }
    setIsSearching(false)
  }

  const onChangeHolding = (text: string) => {
    unstable_batchedUpdates(() => {
      setHoldingsError(false)
      setLocalSelectedHolding(undefined)
      setIsScolapassHolding(false);
      if (localSelectedHolding)
        dispatch(AccountActions.setSelectedHolding(null))
      if (hasBadge !== null) dispatch(AccountActions.setHasBadge(null))
      setSearchValue(text)
    })
  }

  const onSelectHolding = (_selectedHolding: Holding) => {
    unstable_batchedUpdates(() => {
      setLocalSelectedHolding(_selectedHolding)
      dispatch(AccountActions.setSelectedHolding(_selectedHolding))
      dispatch(AccountActions.setHasBadge(true))
      setSearchValue(_selectedHolding.name?.trim() ?? "")
      setHoldingsListShown(false)
      setIsScolapassHolding(_selectedHolding?.isScolaPassCashSystem || false)
    })
  }

  const onCancelPress = () => {
    setSearchValue("")
    setLocalSelectedHolding(undefined)
    setHoldings([])
    setHoldingsListShown(false)
    dispatch(AccountActions.setSelectedHolding(null))
    if (navigation.canGoBack()) {
      navigation.goBack()
      return
    }
    navigation.navigate(HomeScreens.HOME_SCREEN)
  }

  const onActivationPress = async () => {
    if (!localSelectedHolding) {
      return
    }

    setShowError(false)
    setErrorMessage("")

    try {
      await sendBadge(
        {
          idHolding: localSelectedHolding!.id,
          alias: "",
          serialNumber: "",
          guestLastName: "",
          guestValidationCode: "",
          preferredZones: [],
        },
        BadgeLayouts.EstablishmentSelection
      )
    } catch (error) {
      setShowError(true)
      setErrorMessage(I18n.t("associationBadge.globalError"))
      displayToastError(dispatch, I18n.t("associationBadge.toastGlobalError"))
    }
  }

  const isDisabled = () => {
    if (!isEditionMode) {
      return false
    }

    const isSameHolding =
      localSelectedHolding?.id === editionHoldingView?.holding?.id

    return isSameHolding
  }

  const styles = EstablishmentSelectionStyles(isMobile)

  const isOthersCashSystem =
    selectedHolding?.idCashSystem === Number(CashSystem.OTHERS)

  const isSkippable =
    hasBadge === false && selectedHolding?.isProtected === false

  useEffect(() => {
    if (editionHoldingView?.holding && isEditionMode) {
      onSelectHolding({
        ...editionHoldingView.holding,
        preferredZones: editionHoldingView.preferredZones,
      })
    }
  }, [editionHoldingView, isEditionMode])

  return (
    <View style={styles.container}>
      <View style={styles.listContainer}>
        <Title18 isBold style={styles.titleText}>
          {I18n.t(
            "associationBadge.establishmentSelection.establishmentNameTitle"
          )}
        </Title18>
        <Title13 style={styles.subtitleText}>
          {I18n.t(
            isScolapassHolding ? 
            "associationBadge.establishmentSelection.establishmentNameSubtitleScolapass" :
            "associationBadge.establishmentSelection.establishmentNameSubtitle"
          )}
        </Title13>
        <View style={styles.textHoldingInputContainer}>
          <FloatTextInput
            value={searchValue}
            onChangeValue={onChangeHolding}
            textPlaceHolder={I18n.t(
              "associationBadge.establishmentSelection.establishmentName"
            )}
            hasError={holdingsError}
            errorMessage={I18n.t(
              "associationBadge.establishmentSelection.invalidHolding"
            )}
            clearButton
            isLoading={isSearching}
            containerStyles={inputContainerStyles}
          />
        </View>
        {isHoldingsListShown && (
          <ScrollView style={styles.holdingList}>
            {holdings.map((holding, index) => (
              <HoldingRow
                forwardTestID={`${TestIDs.account.actions.holdingRow}_${index}`}
                key={holding.id}
                holding={holding}
                onSelectHolding={onSelectHolding}
              />
            ))}
          </ScrollView>
        )}
      </View>
      {!selectedHolding && (
        <>
          <View style={{ width: "100%" }}>
            <InfoBox
              type='info'
              title={
                <Text style={styles.infoBoxTitle}>
                  {I18n.t("associationBadge.establishmentSelection.tipsTitle")}
                </Text>
              }
              collapsible={true}
              defaultOpen={false}
            >
              <InfoList items={infoListItems} bulletColor={Colors.darkGrey} />
              <Text style={styles.infoBoxHelpStatement}>
                {I18n.t(
                  "associationBadge.establishmentSelection.helpStatement"
                )}
              </Text>
            </InfoBox>
          </View>
          <Button
            type='secondary'
            style={styles.cancelButton}
            onPress={onCancelPress}
          >
            <Text>
              {I18n.t("associationBadge.establishmentSelection.cancel")}
            </Text>
          </Button>
        </>
      )}
      {showError && (
        <InfoBox
          type='danger'
          title={<Text style={styles.infoBoxTitle}>{errorMessage}</Text>}
        />
      )}
      {selectedHolding && (isSkippable || isOthersCashSystem) && (
        <Button
          type='primary'
          style={styles.cancelButton}
          onPress={onActivationPress}
          disabled={isDisabled()}
        >
          <Text>
            {I18n.t("associationBadge.establishmentSelection.activateServices")}
          </Text>
        </Button>
      )}
    </View>
  )
}
